import React, { useState, useEffect, useMemo, useRef } from 'react';
import BillAsSelect from '../features/accounts-receivable/BillAsSelect';
import SendInvoiceModal from '../features/accounts-receivable/components/SendInvoiceModal';
import { accountsReceivableAPI, businessesAPI, customersAPI, inventoryAPI } from '../services/api';
import invoiceApi from '../services/invoiceApi';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';
import { newIdempotencyKey, isPaymentUnconfirmed, PAYMENT_UNCONFIRMED, PAYMENT_REPLAYED } from '../utils/idempotencyKey';
import { todayLocal, localDateKey, parseCalendarDay } from '../utils/localDate';
import { parseMoneyInput, MONEY_FIELD_ERROR } from '../utils/money';

const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired'];
const INVOICE_STATUSES = ['outstanding', 'partially_paid', 'paid', 'disputed', 'written_off'];

const AccountsReceivable = ({ selectedBusiness: propSelectedBusiness, setSelectedBusiness: setGlobalSelectedBusiness, documentType = 'invoice' }) => {
  const isQuote = documentType === 'quote';
  const docLabel = isQuote ? 'Quote' : 'Invoice';
  const [arList, setArList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [agingReport, setAgingReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [paying, setPaying] = useState(false);
  const paymentKey = useRef(null);
  const [localBusiness, setLocalBusiness] = useState(propSelectedBusiness || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [recurringFilter, setRecurringFilter] = useState(''); // '', 'recurring', 'one_time'
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [view, setView] = useState('list'); // 'list' or 'aging'
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAR, setSelectedAR] = useState(null);
  const getEmptyFormData = () => ({
    business_id: propSelectedBusiness || '',
    state_registration_id: '',
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    invoice_number: '',
    invoice_date: todayLocal(),
    due_date: '',
    valid_until_date: '',
    status: isQuote ? 'draft' : '',
    amount: '',
    payment_terms: 'Net 30',
    category: '',
    project_reference: '',
    po_number: '',
    tax_rate: '0',
    discount_type: 'amount',
    discount_rate: '0',
    discount_amount: '0',
    notes: '',
    collection_priority: 'normal',
    is_recurring: false,
    recurrence_frequency: 'monthly',
    next_occurrence_date: '',
    recurrence_max_occurrences: ''
  });
  const [formData, setFormData] = useState(getEmptyFormData());
  const createEmptyLineItem = () => ({ description: '', quantity: '1', unit_price: '0', inventory_item_id: '' });
  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [paymentData, setPaymentData] = useState({
    payment_date: todayLocal(),
    amount: '',
    payment_method: 'check',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBusiness, statusFilter, customerFilter, showOverdueOnly, recurringFilter, documentType]);

  // Sync local selection with Layout's selectedBusiness
  useEffect(() => {
    setLocalBusiness(propSelectedBusiness || '');
  }, [propSelectedBusiness]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { document_type: documentType };
      if (localBusiness) params.business_id = localBusiness;
      if (statusFilter) params.status = statusFilter;
      if (customerFilter) params.customer_name = customerFilter;
      if (showOverdueOnly) params.overdue_only = 'true';
      if (recurringFilter === 'recurring') params.recurring = 'true';
      if (recurringFilter === 'one_time') params.recurring = 'false';

      const [arRes, businessRes, summaryRes, agingRes, customersRes, inventoryRes] = await Promise.all([
        accountsReceivableAPI.getAll(params),
        businessesAPI.getAll(),
        isQuote ? Promise.resolve({ data: null }) : accountsReceivableAPI.getSummary(params),
        isQuote ? Promise.resolve({ data: [] }) : accountsReceivableAPI.getAgingReport(params),
        customersAPI.getAll(params).catch(() => ({ data: [] })),
        inventoryAPI.getAll(localBusiness ? { business_id: localBusiness } : {}).catch(() => ({ data: [] }))
      ]);

      setArList(arRes.data);
      setBusinesses(businessRes.data);
      setSummary(summaryRes.data);
      setAgingReport(agingRes.data);
      setCustomers(customersRes.data);
      setInventoryItems(inventoryRes.data);
      setError('');
    } catch (err) {
      setError(formatApiError(err, 'Failed to load accounts receivable data'));
      console.error('Load error:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    } finally {
      setLoading(false);
    }
  };
  const lineSubtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity);
      const rate = parseFloat(item.unit_price);
      if (isNaN(qty) || isNaN(rate)) {
        return sum;
      }
      return sum + (qty * rate);
    }, 0);
  }, [lineItems]);
  const taxRateValue = Math.min(Math.max(parseFloat(formData.tax_rate || 0) || 0, 0), 100);
  const discountType = formData.discount_type === 'rate' ? 'rate' : 'amount';
  const discountRateValue = Math.min(Math.max(parseFloat(formData.discount_rate || 0) || 0, 0), 100);
  const discountAmountInput = Math.max(parseFloat(formData.discount_amount || 0) || 0, 0);
  const discountAmountValue = discountType === 'rate'
    ? Number(((lineSubtotal * discountRateValue) / 100).toFixed(2))
    : Math.min(discountAmountInput, lineSubtotal);
  const taxableBase = Math.max(lineSubtotal - discountAmountValue, 0);
  const taxAmountValue = Number(((taxableBase * taxRateValue) / 100).toFixed(2));
  const estimatedTotal = Math.max(taxableBase + taxAmountValue, 0);
  const toDateInput = (val) => {
    if (!val) return '';
    // Already yyyy-MM-dd
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };
  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => prev.map((item, idx) => {
      if (idx !== index) return item;
      return {
        ...item,
        [field]: field === 'description' ? value : value.replace(/[^\d.-]/g, '')
      };
    }));
  };
  const handleSelectInventoryItem = (index, inventoryItemId) => {
    setLineItems(prev => prev.map((item, idx) => {
      if (idx !== index) return item;
      if (!inventoryItemId) {
        return { ...item, inventory_item_id: '' };
      }
      const product = inventoryItems.find(p => String(p.id) === String(inventoryItemId));
      return {
        ...item,
        inventory_item_id: inventoryItemId,
        description: product ? product.name : item.description,
        unit_price: product ? String(product.selling_price ?? 0) : item.unit_price
      };
    }));
  };

  const handleAddLineItem = () => {
    setLineItems(prev => [...prev, createEmptyLineItem()]);
  };
  const handleRemoveLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(prev => prev.filter((_, idx) => idx !== index));
  };

  const syncBusinessFilter = (businessId) => {
    const next = businessId ? String(businessId) : '';
    setLocalBusiness(next);
    if (setGlobalSelectedBusiness) {
      setGlobalSelectedBusiness(next ? Number(next) : '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.business_id) {
        setError(`Please select a business for this ${docLabel.toLowerCase()}.`);
        return;
      }

      const sanitizedLineItems = lineItems
        .map((item, idx) => {
          const description = (item.description || '').trim();
          const quantity = parseFloat(item.quantity);
          const unit_price = parseFloat(item.unit_price);
          if (!description || isNaN(quantity) || quantity === 0 || isNaN(unit_price)) {
            return null;
          }
          const line_total = quantity * unit_price;
          return {
            line_order: idx + 1,
            description,
            quantity,
            unit_price,
            line_total,
            inventory_item_id: item.inventory_item_id ? Number(item.inventory_item_id) : null
          };
        })
        .filter(Boolean);

      if (!sanitizedLineItems.length) {
        setError('Please add at least one line item with a description, quantity, and rate.');
        return;
      }

      const totalAmount = Number(estimatedTotal.toFixed(2));
      const businessId = Number(formData.business_id);
      const { _suggestedNumber, ...formFields } = formData;

      const payload = {
        ...formFields,
        business_id: businessId,
        document_type: documentType,
        status: formData.status || undefined,
        amount: totalAmount,
        tax_rate: taxRateValue,
        tax_amount: taxAmountValue,
        discount_type: discountType,
        discount_rate: discountType === 'rate' ? discountRateValue : 0,
        discount_amount: discountAmountValue,
        invoice_date: toDateInput(formData.invoice_date),
        // Fallback: if DB still enforces NOT NULL, use next_occurrence_date as due_date for recurring
        due_date: isQuote ? null : (toDateInput(formData.due_date) || (formData.is_recurring ? toDateInput(formData.next_occurrence_date) : null)),
        valid_until_date: isQuote ? (toDateInput(formData.valid_until_date) || null) : null,
        next_occurrence_date: (!isQuote && formData.is_recurring) ? toDateInput(formData.next_occurrence_date) : null,
        recurrence_max_occurrences: formData.recurrence_max_occurrences
          ? Number(formData.recurrence_max_occurrences)
          : null,
        line_items: sanitizedLineItems
      };
      if (selectedAR) {
        await accountsReceivableAPI.update(selectedAR.id, payload);
      } else {
        await accountsReceivableAPI.create(payload);
      }
      setShowModal(false);
      resetForm();
      // List is filtered by selected business — if the saved record belongs to a
      // different business than the current filter, align the filter so it appears.
      if (String(localBusiness || '') !== String(businessId)) {
        syncBusinessFilter(businessId);
      } else {
        loadData();
      }
    } catch (err) {
      setError(formatApiError(err, `Failed to save ${docLabel.toLowerCase()}`));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (paying) return;
    const paymentAmount = parseMoneyInput(paymentData.amount);
    if (paymentAmount.error || paymentAmount.value === null) {
      setError(`Amount: ${MONEY_FIELD_ERROR}`);
      return;
    }
    if (!paymentKey.current) paymentKey.current = newIdempotencyKey();
    setPaying(true);
    try {
      const payload = {
        ...paymentData,
        payment_date: toDateInput(paymentData.payment_date),
        amount: paymentAmount.value,
        idempotency_key: paymentKey.current,
      };
      const response = await accountsReceivableAPI.recordPayment(selectedAR.id, payload);
      paymentKey.current = null;
      setNotice(response.data?.replayed ? PAYMENT_REPLAYED : '');
      setShowPaymentModal(false);
      setPaymentData({
        payment_date: todayLocal(),
        amount: '',
        payment_method: 'check',
        reference_number: '',
        notes: ''
      });
      loadData();
    } catch (err) {
      if (isPaymentUnconfirmed(err)) setError(PAYMENT_UNCONFIRMED);
      else setError(formatApiError(err, 'Failed to record payment'));
    } finally {
      setPaying(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${docLabel.toLowerCase()}?`)) return;
    try {
      await accountsReceivableAPI.delete(id);
      loadData();
    } catch (err) {
      setError(formatApiError(err, `Failed to delete ${docLabel.toLowerCase()}`));
    }
  };

  const handleConvertToInvoice = async (ar) => {
    if (!window.confirm(`Convert quote ${ar.invoice_number} to an invoice?`)) return;
    try {
      await accountsReceivableAPI.convertToInvoice(ar.id);
      loadData();
    } catch (err) {
      setError(formatApiError(err, 'Failed to convert quote to invoice'));
    }
  };

  const openEditModal = (ar) => {
    setSelectedAR(ar);
    setFormData({
      customer_id: ar.customer_id || '',
      business_id: ar.business_id || '',
      state_registration_id: ar.state_registration_id ? String(ar.state_registration_id) : '',
      customer_name: ar.customer_name,
      customer_email: ar.customer_email || '',
      customer_phone: ar.customer_phone || '',
      customer_address: ar.customer_address || '',
      invoice_number: ar.invoice_number,
      invoice_date: toDateInput(ar.invoice_date),
      due_date: toDateInput(ar.due_date),
      valid_until_date: toDateInput(ar.valid_until_date),
      status: ar.status || (isQuote ? 'draft' : ''),
      amount: ar.amount,
      payment_terms: ar.payment_terms || 'Net 30',
      category: ar.category || '',
      project_reference: ar.project_reference || '',
      po_number: ar.po_number || '',
      tax_rate: ar.tax_rate != null && ar.tax_rate !== '' ? String(ar.tax_rate) : '0',
      discount_type: ar.discount_type === 'rate' ? 'rate' : 'amount',
      discount_rate: ar.discount_rate != null && ar.discount_rate !== '' ? String(ar.discount_rate) : '0',
      discount_amount: ar.discount_amount != null && ar.discount_amount !== '' ? String(ar.discount_amount) : '0',
      notes: ar.notes || '',
      collection_priority: ar.collection_priority || 'normal',
      is_recurring: ar.is_recurring || false,
      recurrence_frequency: ar.recurrence_frequency || 'monthly',
      next_occurrence_date: toDateInput(ar.next_occurrence_date),
      recurrence_max_occurrences: ar.recurrence_max_occurrences || ''
    });
    setLineItems(
      ar.line_items && ar.line_items.length
        ? ar.line_items.map(item => ({
            description: item.description || '',
            quantity: item.quantity !== undefined && item.quantity !== null ? String(item.quantity) : '1',
            unit_price: item.unit_price !== undefined && item.unit_price !== null
              ? String(item.unit_price)
              : item.line_total !== undefined
                ? String(item.line_total)
                : '0',
            inventory_item_id: item.inventory_item_id || ''
          }))
        : [createEmptyLineItem()]
    );
    setShowModal(true);
  };

  const openPaymentModal = (ar) => {
    paymentKey.current = newIdempotencyKey();
    setNotice('');
    setSelectedAR(ar);
    setPaymentData({
      ...paymentData,
      amount: ar.amount_remaining || (ar.amount - ar.amount_paid)
    });
    setShowPaymentModal(true);
  };

  const [sendingInvoice, setSendingInvoice] = useState(null);
  const handleInvoiceSent = (out) => {
    setSendingInvoice(null);
    setNotice(out.pay_link ? `Invoice emailed to ${out.to} with a Pay now link.` : `Invoice emailed to ${out.to}. It has no Pay now button: ${out.pay_link_note || 'online payment is not available.'}`);
    loadData();
  };

  const downloadInvoicePDF = async (ar) => {
    try {
      const response = await invoiceApi.downloadPDF(ar.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${ar.document_type === 'quote' ? 'quote' : 'invoice'}-${ar.invoice_number || ar.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err, `Failed to download ${docLabel.toLowerCase()}`));
    }
  };

  const resetForm = () => {
    setSelectedAR(null);
    setFormData({ ...getEmptyFormData(), business_id: localBusiness || '' });
    setLineItems([createEmptyLineItem()]);
  };

  const prefillDocumentNumber = async (businessId, { preserveEdited = false, stateRegistrationId = '' } = {}) => {
    if (!businessId || selectedAR) return;
    try {
      const res = await accountsReceivableAPI.getNextNumber({
        business_id: businessId,
        document_type: documentType,
        ...(stateRegistrationId ? { state_registration_id: stateRegistrationId } : {})
      });
      const suggested = res.data?.number || '';
      if (!suggested) return;
      setFormData((prev) => {
        if (preserveEdited && prev.invoice_number && prev.invoice_number !== prev._suggestedNumber) {
          return prev;
        }
        return { ...prev, invoice_number: suggested, _suggestedNumber: suggested };
      });
    } catch (err) {
      console.error('Failed to prefill document number:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const openCreateModal = async () => {
    resetForm();
    setShowModal(true);
    const businessId = localBusiness || '';
    if (businessId) {
      await prefillDocumentNumber(businessId);
    }
  };

  const getStatusBadge = (ar) => {
    if (ar.document_type === 'quote') {
      switch (ar.status) {
        case 'sent':
          return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Sent</span>;
        case 'accepted':
          return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Accepted</span>;
        case 'declined':
          return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Declined</span>;
        case 'expired':
          return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Expired</span>;
        default:
          return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">Draft</span>;
      }
    }
    const isOverdue = ar.is_overdue || (!!ar.due_date && parseCalendarDay(ar.due_date) < new Date() && ar.amount_paid < ar.amount);
    if (isOverdue) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Overdue</span>;
    }
    switch (ar.status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Paid</span>;
      case 'partially_paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Partial</span>;
      case 'written_off':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Written Off</span>;
      case 'disputed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">Disputed</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Outstanding</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getAgingDays = (ar) => {
    if (ar.aging_days !== undefined && ar.aging_days !== null) return ar.aging_days;
    if (!ar.due_date) return 0;
    const dueDate = parseCalendarDay(ar.due_date);
    if (isNaN(dueDate.getTime())) return 0;
    const today = new Date();
    return Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
  };

  if (loading && !arList.length) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{isQuote ? 'Quotes' : 'Accounts Receivable'}</h1>
          <p className="text-gray-600 mt-1">{isQuote ? 'Create and track customer quotes' : 'Manage invoices and track customer payments'}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          + New {docLabel}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded" role="status">
          {notice}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <ErrorBoundary>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Billed</div>
              <div className="text-2xl font-bold text-gray-800">{formatCurrency(summary.total_billed)}</div>
              <div className="text-xs text-gray-500 mt-1">{summary.total_invoices} invoices</div>
            </div>
          </ErrorBoundary>
          <ErrorBoundary>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Collected</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_collected)}</div>
            </div>
          </ErrorBoundary>
          <ErrorBoundary>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Outstanding</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_outstanding)}</div>
              <div className="text-xs text-gray-500 mt-1">{summary.outstanding_count} invoices</div>
            </div>
          </ErrorBoundary>
          <ErrorBoundary>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Overdue</div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.overdue_amount)}</div>
              <div className="text-xs text-gray-500 mt-1">{summary.overdue_count} invoices</div>
            </div>
          </ErrorBoundary>
          <ErrorBoundary>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Paid</div>
              <div className="text-2xl font-bold text-gray-600">{summary.paid_count}</div>
              <div className="text-xs text-gray-500 mt-1">invoices</div>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Aging Summary */}
      {summary && (
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-3">Aging Summary</h3>
          <div className="grid grid-cols-4 gap-4">
            <ErrorBoundary>
              <div className="text-center">
                <div className="text-sm text-gray-600">Current (0-30)</div>
                <div className="text-xl font-bold text-green-600">{formatCurrency(summary.current_0_30)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="text-center">
                <div className="text-sm text-gray-600">31-60 Days</div>
                <div className="text-xl font-bold text-yellow-600">{formatCurrency(summary.aging_31_60)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="text-center">
                <div className="text-sm text-gray-600">61-90 Days</div>
                <div className="text-xl font-bold text-orange-600">{formatCurrency(summary.aging_61_90)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="text-center">
                <div className="text-sm text-gray-600">Over 90 Days</div>
                <div className="text-xl font-bold text-red-600">{formatCurrency(summary.aging_over_90)}</div>
              </div>
            </ErrorBoundary>
          </div>
        </div>
      )}

      {/* Filters and View Toggle */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-4 flex-wrap">
            <ErrorBoundary fallback={<span className="text-xs text-red-600">Business filter unavailable</span>}>
            <select
              value={localBusiness}
              onChange={(e) => {
                const val = e.target.value;
                setLocalBusiness(val);
                if (setGlobalSelectedBusiness) {
                  // keep global selection in sync
                  setGlobalSelectedBusiness(val ? Number(val) : '');
                }
              }}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Businesses</option>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            </ErrorBoundary>

            <ErrorBoundary fallback={<span className="text-xs text-red-600">Status filter unavailable</span>}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              {(isQuote ? QUOTE_STATUSES : INVOICE_STATUSES).map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
            </ErrorBoundary>

            <ErrorBoundary fallback={<span className="text-xs text-red-600">Search unavailable</span>}>
            <input
              type="text"
              placeholder="Search customer..."
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            />
            </ErrorBoundary>

            {!isQuote && (
              <ErrorBoundary fallback={<span className="text-xs text-red-600">Overdue filter unavailable</span>}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showOverdueOnly}
                  onChange={(e) => setShowOverdueOnly(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Overdue Only</span>
              </label>
              </ErrorBoundary>
            )}

            {!isQuote && (
              <ErrorBoundary fallback={<span className="text-xs text-red-600">Type filter unavailable</span>}>
                <select
                  value={recurringFilter}
                  onChange={(e) => setRecurringFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">All Types</option>
                  <option value="recurring">Recurring Only</option>
                  <option value="one_time">One-time Only</option>
                </select>
              </ErrorBoundary>
            )}
          </div>

          {!isQuote && (
            <div className="flex gap-2">
              <button
                onClick={() => setView('list')}
                className={`px-4 py-2 rounded ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Invoice List
              </button>
              <button
                onClick={() => setView('aging')}
                className={`px-4 py-2 rounded ${view === 'aging' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Aging Report
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Invoice List View */}
      {view === 'list' && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{docLabel} #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{isQuote ? 'Valid Until' : 'Due Date'}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                {!isQuote && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>}
                {!isQuote && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Outstanding</th>}
                {!isQuote && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aging</th>}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {arList.map((ar) => {
                const amountRemaining = ar.amount_remaining || (ar.amount - ar.amount_paid);
                const agingDays = getAgingDays(ar);
                return (
                  <tr key={ar.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>{ar.invoice_number}</ErrorBoundary>
                        {!isQuote && (
                          <ErrorBoundary fallback={<span className="text-red-600 text-xs"> </span>}>
                            {ar.is_recurring ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                {(() => {
                                  const max = ar.recurrence_max_occurrences;
                                  const done = ar.recurrence_occurrences_completed || 0;
                                  return (typeof max === 'number' && max > 0)
                                    ? `Recurring (${Math.min(done, max)}/${max})`
                                    : 'Recurring (∞)';
                                })()}
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">One-time</span>
                            )}
                          </ErrorBoundary>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>
                        {businesses.find(b => Number(b.id) === Number(ar.business_id))?.name || (ar.business_id ? `Business #${ar.business_id}` : '—')}
                      </ErrorBoundary>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>{ar.customer_name}</ErrorBoundary>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>{ar.invoice_date}</ErrorBoundary>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>{isQuote ? (ar.valid_until_date || '-') : ar.due_date}</ErrorBoundary>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(ar.amount)}</ErrorBoundary>
                    </td>
                    {!isQuote && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        <ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(ar.amount_paid)}</ErrorBoundary>
                      </td>
                    )}
                    {!isQuote && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold">
                        <ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(amountRemaining)}</ErrorBoundary>
                      </td>
                    )}
                    {!isQuote && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <ErrorBoundary fallback={<span className="text-red-600 text-xs">?</span>}>
                          <span className={`font-semibold ${agingDays > 90 ? 'text-red-600' : agingDays > 60 ? 'text-orange-600' : agingDays > 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {agingDays} days
                          </span>
                        </ErrorBoundary>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">Status</span>}>{getStatusBadge(ar)}</ErrorBoundary>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>
                        <button
                          onClick={() => downloadInvoicePDF(ar)}
                          className="text-purple-600 hover:text-purple-900 mr-2"
                          title={`Download PDF ${docLabel}`}
                        >
                          📄 PDF
                        </button>
                        {!isQuote && ar.status !== 'draft' && (
                          <button
                            onClick={() => setSendingInvoice(ar)}
                            className="text-indigo-600 hover:text-indigo-900 mr-2"
                            title="Email this invoice to the customer"
                          >
                            Send
                          </button>
                        )}
                        {!isQuote && ar.status !== 'paid' && (
                          <button
                            onClick={() => openPaymentModal(ar)}
                            className="text-green-600 hover:text-green-900 mr-2"
                          >
                            Pay
                          </button>
                        )}
                        {isQuote && ar.status === 'accepted' && (
                          <button
                            onClick={() => handleConvertToInvoice(ar)}
                            className="text-green-600 hover:text-green-900 mr-2"
                          >
                            Convert to Invoice
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(ar)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(ar.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </ErrorBoundary>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {arList.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No {isQuote ? 'quotes' : 'invoices'} found
              {localBusiness ? ' for the selected business. Try “All Businesses” if you expected to see one.' : '.'}
            </div>
          )}
        </div>
      )}

      {/* Aging Report View */}
      {!isQuote && view === 'aging' && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">0-30 Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Over 90 Days</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agingReport.map((customer) => (
                <tr key={customer.customer_name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"><ErrorBoundary fallback={<span className="text-red-600 text-xs">-</span>}>{customer.customer_name}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><ErrorBoundary fallback={<span className="text-red-600 text-xs">0</span>}>{customer.invoice_count}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.total_amount)}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.total_outstanding)}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.current_0_30)}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.aging_31_60)}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.aging_61_90)}</ErrorBoundary></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600"><ErrorBoundary fallback={<span className="text-red-600 text-xs">$0.00</span>}>{formatCurrency(customer.aging_over_90)}</ErrorBoundary></td>
                </tr>
              ))}
            </tbody>
          </table>
          {agingReport.length === 0 && (
            <div className="text-center py-8 text-gray-500">No aging data available</div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">{selectedAR ? `Edit ${docLabel}` : `New ${docLabel}`}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business *</label>
                  <select
                    required
                    value={formData.business_id}
                    onChange={(e) => {
                      const businessId = e.target.value;
                      setFormData({ ...formData, business_id: businessId, state_registration_id: '' });
                      if (!selectedAR && businessId) {
                        prefillDocumentNumber(businessId);
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">Select business</option>
                    {businesses.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <BillAsSelect
                  businessId={formData.business_id}
                  businessName={businesses.find(b => Number(b.id) === Number(formData.business_id))?.name}
                  value={formData.state_registration_id}
                  onChange={(stateRegistrationId) => {
                    setFormData((prev) => ({ ...prev, state_registration_id: stateRegistrationId }));
                    if (!selectedAR && formData.business_id) {
                      prefillDocumentNumber(formData.business_id, { preserveEdited: true, stateRegistrationId });
                    }
                  }}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{docLabel} Number *</label>
                  <input
                    type="text"
                    required
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                  {!selectedAR && (
                    <p className="text-xs text-gray-500 mt-1">Suggested number — you can change it.</p>
                  )}
                </div>
              </div>

              {isQuote && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    {QUOTE_STATUSES.map(s => (
                      <option key={s} value={s}>{s.replace(/\b\w/g, c => c.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const c = customers.find(x => String(x.id) === String(id));
                      setFormData({
                        ...formData,
                        customer_id: id,
                        customer_name: c ? c.customer_name : formData.customer_name,
                        customer_email: c ? (c.email || formData.customer_email) : formData.customer_email,
                        customer_phone: c ? (c.phone || formData.customer_phone) : formData.customer_phone,
                      });
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">-- Optional: Link existing customer --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.customer_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Phone</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Net 90">Net 90</option>
                    <option value="Perpetual Subscription">Perpetual Subscription</option>
                    <option value="Installments">Installments</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Address</label>
                <textarea
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{docLabel} Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                {isQuote ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                    <input
                      type="date"
                      value={formData.valid_until_date}
                      onChange={(e) => setFormData({ ...formData, valid_until_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date {formData.is_recurring ? '(optional for recurring)' : '*'}</label>
                    <input
                      type="date"
                      required={!formData.is_recurring}
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Line Items *</label>
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    + Add line
                  </button>
                </div>
                <div className="space-y-4">
                  {lineItems.map((item, index) => {
                    const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                    const selectedProduct = item.inventory_item_id
                      ? inventoryItems.find(p => String(p.id) === String(item.inventory_item_id))
                      : null;
                    const unitLabel = selectedProduct?.unit_of_measure;
                    return (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 border border-gray-200 rounded-lg p-3">
                        <div className="md:col-span-5">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          {inventoryItems.length > 0 && (
                            <select
                              value={item.inventory_item_id || ''}
                              onChange={(e) => handleSelectInventoryItem(index, e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2 mb-2 text-sm"
                            >
                              <option value="">-- Choose a saved product/service (optional) --</option>
                              {inventoryItems.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                  {p.selling_price ? ` — ${formatCurrency(p.selling_price)}${p.item_type === 'service' && p.unit_of_measure ? ` / ${p.unit_of_measure}` : ''}` : ''}
                                </option>
                              ))}
                            </select>
                          )}
                          <input
                            type="text"
                            required
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity{unitLabel ? ` (${unitLabel})` : ''}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rate{unitLabel ? ` (per ${unitLabel})` : ''}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Line Total</label>
                          <div className="w-full border border-gray-200 rounded px-3 py-2 bg-gray-50 text-sm text-right">
                            {formatCurrency(lineTotal)}
                          </div>
                        </div>
                        <div className="md:col-span-1 flex justify-end items-end">
                          {lineItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveLineItem(index)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Line Subtotal</span>
                  <span className="font-semibold">{formatCurrency(lineSubtotal)}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tax amount: {formatCurrency(taxAmountValue)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                  <div className="flex gap-2">
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        discount_type: e.target.value,
                        discount_amount: '0',
                        discount_rate: '0'
                      })}
                      className="w-28 border border-gray-300 rounded px-2 py-2 text-sm"
                    >
                      <option value="amount">Amount ($)</option>
                      <option value="rate">Rate (%)</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={formData.discount_type === 'rate' ? '100' : undefined}
                      value={formData.discount_type === 'rate' ? formData.discount_rate : formData.discount_amount}
                      onChange={(e) => setFormData({
                        ...formData,
                        ...(formData.discount_type === 'rate'
                          ? { discount_rate: e.target.value }
                          : { discount_amount: e.target.value })
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.discount_type === 'rate'
                      ? `Discount amount: ${formatCurrency(discountAmountValue)}`
                      : 'Fixed dollar discount'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Total</label>
                  <div className="w-full border border-gray-200 rounded px-3 py-2 bg-gray-50 text-sm font-semibold text-right">
                    {formatCurrency(estimatedTotal)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Subtotal − discount + tax</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="e.g., Services, Products"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Collection Priority</label>
                  <select
                    value={formData.collection_priority}
                    onChange={(e) => setFormData({ ...formData, collection_priority: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Reference</label>
                  <input
                    type="text"
                    value={formData.project_reference}
                    onChange={(e) => setFormData({ ...formData, project_reference: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                  <input
                    type="text"
                    value={formData.po_number}
                    onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="3"
                />
              </div>

              {/* Recurring Income Section (invoices only) */}
              {!isQuote && (
                <div className="border-t pt-4 mt-4">
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-blue-900 mb-2">💰 Recurring Income</h4>
                    <p className="text-sm text-blue-700">
                      Set up automatic monthly income sources like retirement, disability, or regular customer payments
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      id="is_recurring"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({
                        ...formData,
                        is_recurring: e.target.checked,
                        next_occurrence_date: e.target.checked && !formData.next_occurrence_date
                          ? localDateKey(new Date(new Date().setMonth(new Date().getMonth() + 1)))
                          : formData.next_occurrence_date
                      })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                      This is a recurring income source
                    </label>
                  </div>

                  {formData.is_recurring && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                        <select
                          value={formData.recurrence_frequency}
                          onChange={(e) => setFormData({ ...formData, recurrence_frequency: e.target.value })}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                          {[['weekly', 'Weekly'], ['biweekly', 'Every 2 weeks'], ['monthly', 'Monthly'], ['quarterly', 'Quarterly (Every 3 months)'], ['semi_annual', 'Every 6 months'], ['yearly', 'Yearly (Annual)']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Next Expected Date</label>
                        <input
                          type="date"
                          value={formData.next_occurrence_date}
                          onChange={(e) => setFormData({ ...formData, next_occurrence_date: e.target.value })}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                          required={formData.is_recurring}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Payments (optional)</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="Leave blank for perpetual"
                          value={formData.recurrence_max_occurrences}
                          onChange={(e) => setFormData({ ...formData, recurrence_max_occurrences: e.target.value })}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">Blank = continues forever</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {selectedAR ? 'Update' : 'Create'} {docLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {sendingInvoice && (
        <SendInvoiceModal invoice={sendingInvoice} onClose={() => setSendingInvoice(null)} onSent={handleInvoiceSent} />
      )}

      {showPaymentModal && selectedAR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Record Payment</h2>
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <div className="text-sm text-gray-600">Invoice: {selectedAR.invoice_number}</div>
              <div className="text-sm text-gray-600">Customer: {selectedAR.customer_name}</div>
              <div className="text-lg font-semibold mt-2">Outstanding: {formatCurrency(selectedAR.amount_remaining || (selectedAR.amount - selectedAR.amount_paid))}</div>
            </div>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  required
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                <select
                  required
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="paypal">PayPal</option>
                  <option value="venmo">Venmo</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                <input
                  type="text"
                  value={paymentData.reference_number}
                  onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Check #, Confirmation #, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    paymentKey.current = null;
                    setShowPaymentModal(false);
                    setPaymentData({
                      payment_date: todayLocal(),
                      amount: '',
                      payment_method: 'check',
                      reference_number: '',
                      notes: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={paying}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsReceivable;
